import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { PageHeader, btn, TextField, TextArea, SelectField, ErrorSummary, ErrorBox } from "../components/kit";
import { api, errMsg } from "../lib/api";

const Prose = ({ children }) => <div className="container-x py-10"><div className="prose-a max-w-3xl text-lg">{children}</div></div>;
const DRAFT = (
  <p className="rounded-lg border border-[#E8B66B] bg-[#FFF4E5] p-4 text-base text-[#5C3100]">
    Draft policy text for review by OpenPath's legal adviser before launch.
  </p>
);

export const About = () => (
  <>
    <PageHeader
      eyebrow="About"
      title="About OpenPath"
      description="Making career opportunities, professional connections and relevant government resources easier to discover and access."
    />
    <Prose>
      <h2>Our mission</h2>
      <p>
        OpenPath brings jobs, freelance work, government opportunities and a professional community
        together in one accessible place. One account gives you access to every section that is relevant
        to you.
      </p>
      <h2>Who it's for</h2>
      <ul><li>Job seekers, from fresh graduates to experienced professionals.</li><li>Freelancers and independent professionals.</li><li>Persons with disabilities seeking accessible career opportunities, schemes and benefits.</li><li>Employers, clients, recruiters, mentors and career counsellors.</li></ul>
      <h2>How we build trust</h2>
      <ul><li>Listings are reviewed by moderators before they are published.</li><li>Government records show their official source and last-verified date.</li><li>We never display verification badges without a real review, and never publish invented statistics or testimonials.</li><li>Sensitive information such as disability or accommodation needs is private by default and never required.</li></ul>
    </Prose>
  </>
);

export const AccessibilityPage = () => (
  <>
    <PageHeader
      eyebrow="Accessibility"
      title="Accessibility statement"
      description="Accessibility is a core product requirement at OpenPath, not an add-on."
    />
    <Prose>
      <h2>Our target</h2>
      <p>We are working towards conformance with the Web Content Accessibility Guidelines (WCAG) 2.2 Level AA. <strong>We do not yet claim conformance:</strong> a full automated and manual audit, including testing with assistive technology users, is still to be completed.</p>
      <h2>What we have built in</h2>
      <ul>
        <li>Semantic HTML, a logical heading structure and a "Skip to main content" link.</li>
        <li>Full keyboard navigation with a clearly visible focus indicator.</li>
        <li>Labelled form fields, error summaries and inline error messages that do not rely on colour alone.</li>
        <li>Status announcements for search results and form outcomes.</li>
        <li>Touch targets of at least 44 × 44 CSS pixels on key controls.</li>
        <li>Respect for your operating system's reduced-motion preference.</li>
        <li>Password managers and pasting are fully supported on sign-in forms.</li>
      </ul>
      <h2>Colour contrast checks</h2>
      <p>Calculated contrast ratios for the core palette (WCAG relative luminance):</p>
      <ul>
        <li>Main text #172B3A on white: about 14.6:1 (passes AA).</li>
        <li>Secondary text #526273 on white: about 6.1:1; on #F2F5F9: about 5.7:1 (passes AA).</li>
        <li>White text on primary blue #246BFE buttons: about 4.6:1 (passes AA for normal text).</li>
        <li>Primary blue #246BFE as text on #F2F5F9 is about 4.2:1 and fails AA, so links use the darker #1D55CA (about 6.6:1 on white).</li>
        <li>The border colour #D5DEE8 is below 3:1, so form fields use a darker #6B7C8F border (about 4.4:1) to stay identifiable.</li>
        <li>Error #B42318 on white: about 6.5:1. Success green #2E7D32 on white: about 5.1:1. Community purple #7B3FE4 on white: about 5.7:1.</li>
      </ul>
      <h2>Personalise your experience</h2>
      <p>Use the Accessibility button in the header (or press Alt + Shift + A) to increase text size, increase contrast, reduce motion, widen line spacing or switch to a simplified layout. Signed-in users have these preferences saved to their account.</p>
      <h2>Media</h2>
      <p>Any video or audio content published on OpenPath must include captions or a transcript.</p>
      <h2>Feedback</h2>
      <p>If you find a barrier, please <Link className="link" to="/contact">contact us</Link> and choose "Accessibility issue". We aim to respond within five working days.</p>
    </Prose>
  </>
);

export const ForEmployers = () => (
  <>
    <PageHeader
      eyebrow="For employers"
      title="Hire inclusively with OpenPath"
      description="Post roles, describe your workplace accessibility and accommodation policies, and manage applicants securely."
    >
      <Link to="/signup?role=employer" className={btn()} data-testid="employers-signup-btn">Create an employer account</Link>
      <Link to="/employer" className={btn("secondary")}>Go to employer hub</Link>
    </PageHeader>
    <Prose>
      <h2>What you can do</h2>
      <ul><li>Create a company profile and post job listings (reviewed by moderators before publishing).</li><li>Share genuine workplace accessibility information and your accommodation process.</li><li>Review applications, CVs and candidate profiles for your own listings only.</li><li>Update application statuses — candidates are notified in-app.</li><li>Close or archive vacancies when they are filled.</li></ul>
      <h2>Our standards</h2>
      <p>
        Only describe accessibility features and accommodations that genuinely exist. OpenPath does not
        add or verify accessibility claims on your behalf. Listings that are misleading may be removed.
      </p>
    </Prose>
  </>
);

export const ForProfessionals = () => (
  <>
    <PageHeader eyebrow="For professionals" title="Professionals and mentors" description="Recruiters, career counsellors, coaches, accessibility specialists and legal advisers can support people through the directory.">
      <Link to="/settings?tab=professional" className={btn("community")} data-testid="professionals-create-profile-btn">Create a professional profile</Link>
    </PageHeader>
    <Prose>
      <h2>How it works</h2>
      <ul><li>Create a professional profile describing your expertise, languages, availability and whether help is free, paid or volunteer-based.</li><li>Receive mentorship, resume-review, interview-preparation and guidance requests in your dashboard.</li><li>Accept or decline requests and add a note with next steps.</li></ul>
      <h2>Verification</h2>
      <p>
        You can request verification by describing your credentials. A badge is only shown after an
        OpenPath administrator has reviewed and approved your request.
      </p>
      <h2>Bookings and payments</h2>
      <p>In-platform scheduling, messaging and payments are not available yet. Sessions are arranged directly between you and the requester after you accept a request.</p>
    </Prose>
  </>
);

const FAQ = [
  ["Do I need separate accounts for jobs, freelancing and the community?", "No. One OpenPath account gives you access to every section. Add or remove roles at any time in Settings."],
  ["Do I have to disclose a disability?", "Never. Disclosure is never required to create an account, search, apply or join the community. If you choose to share accommodation needs, they stay private unless you include them in a specific application."],
  ["Are the government listings official?", "Each listing shows its official source and last-verified date. Records marked SAMPLE are for demonstration only. Always check the official notification before applying."],
  ["How do I withdraw an application?", "Open Dashboard → My applications and choose Withdraw on the application."],
  ["Are payments for freelance projects handled by OpenPath?", "Not yet. Payments are agreed and made outside OpenPath. There is no escrow or payment protection at this time."],
  ["Are email notifications sent?", "Account emails (verification and password reset) are sent by email. Other notifications currently appear in-app only; SMS and WhatsApp alerts are pending configuration."],
  ["How do I report a listing or post?", "Use the Report button on any job, project, post, comment or profile. Moderators review every report."],
];
export const Help = () => (
  <>
    <PageHeader eyebrow="Help Centre" title="Help Centre & FAQs" description="Answers to common questions. Still stuck? Contact our support team." ><Link to="/contact" className={btn()}>Contact support</Link></PageHeader>
    <div className="container-x py-10 max-w-3xl">
      <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} className="card px-5">
            <AccordionTrigger className="text-left text-lg font-semibold text-navy min-h-[56px]" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-base text-ink">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </>
);

export function Contact() {
  const [f, setF] = useState({ name: "", email: "", topic: "", message: "" });
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState("");
  const [done, setDone] = useState("");
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.name.trim().length < 2) er["contact-name"] = "Enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) er["contact-email"] = "Enter a valid email address, like name@example.com.";
    if (!f.topic) er["contact-topic"] = "Choose a topic.";
    if (f.message.trim().length < 10) er["contact-message"] = "Write a message of at least 10 characters.";
    setErrors(er);
    if (Object.keys(er).length) return;
    try { const { data } = await api.post("/contact", f); setDone(data.message); toast.success("Message sent"); } catch (e2) { setServerErr(errMsg(e2)); }
  };
  return (
    <>
      <PageHeader eyebrow="Contact" title="Contact us" description="Questions, accessibility barriers, partnership enquiries or feedback — we read every message." />
      <div className="container-x py-10 max-w-2xl">
        {done ? <div role="status" className="card p-6 border-green" data-testid="contact-success"><h2 className="text-xl font-bold text-green">Message sent</h2><p className="mt-2">{done}</p></div> : (
          <form onSubmit={submit} noValidate className="space-y-5" data-testid="contact-form">
            <ErrorSummary errors={errors} />
            <TextField id="contact-name" label="Your name" required autoComplete="name" value={f.name} onChange={on("name")} error={errors["contact-name"]} />
            <TextField id="contact-email" label="Email address" type="email" required autoComplete="email" value={f.email} onChange={on("email")} error={errors["contact-email"]} />
            <SelectField id="contact-topic" label="Topic" required value={f.topic} onChange={on("topic")} error={errors["contact-topic"]} placeholder="Choose a topic" options={["General question", "Accessibility issue", "Account help", "Employer enquiry", "Report a problem", "Partnership"]} />
            <TextArea id="contact-message" label="Message" required rows={6} value={f.message} onChange={on("message")} error={errors["contact-message"]} />
            {serverErr && <ErrorBox message={serverErr} />}
            <button className={btn()} data-testid="contact-submit-btn">Send message</button>
          </form>
        )}
      </div>
    </>
  );
}

export const Privacy = () => (
  <>
    <PageHeader eyebrow="Legal" title="Privacy Policy" />
    <Prose>{DRAFT}
      <h2>What we collect</h2><p>Account details (name, email), profile information you choose to add, applications, proposals, community content, and accessibility display preferences.</p>
      <h2>Sensitive information</h2><p>We never require you to disclose a disability. Accommodation notes are optional, private by default and only shared with an employer when you include them in a specific application.</p>
      <h2>Documents</h2><p>CVs are stored in protected storage and can only be downloaded by you, the employer for that listing, and administrators.</p>
      <h2>Your controls</h2><p>You control which profile fields are public in Settings → Privacy. You can request a copy or deletion of your data through the contact page.</p>
    </Prose>
  </>
);
export const Terms = () => (
  <>
    <PageHeader eyebrow="Legal" title="Terms and Conditions" />
    <Prose>{DRAFT}
      <h2>Using OpenPath</h2><p>You agree to provide accurate information and to follow the Community Guidelines. OpenPath does not guarantee interviews, referrals, jobs, projects or placement outcomes.</p>
      <h2>Listings</h2><p>Employers and clients are responsible for the accuracy of their listings, including any accessibility claims. Government information must be confirmed against the official notification.</p>
      <h2>Payments</h2><p>OpenPath does not currently process payments or offer escrow. Any payment arrangements are made directly between users.</p>
      <h2>Moderation</h2><p>We may remove content or restrict accounts that break these terms.</p>
    </Prose>
  </>
);
export const Guidelines = () => (
  <>
    <PageHeader
      eyebrow="Community"
      title="Community Guidelines"
      description="OpenPath Community is a welcoming, professional space. These guidelines help keep it that way."
    />
    <Prose>
      <h2>Be respectful</h2><ul><li>No harassment, hate speech, discrimination or ableist language.</li><li>Respect privacy — never share someone else's personal or medical information.</li></ul>
      <h2>Be clear about what you share</h2><p>Label posts as personal experience, professional opinion or verified fact. Link to official sources for government information.</p>
      <h2>No spam or scams</h2><p>No unsolicited promotion, fee-based job offers or requests for payment to apply.</p>
      <h2>Referrals and mentorship</h2><p>Requests are welcome but never guaranteed. Accept a "no" gracefully.</p>
      <h2>Reporting and blocking</h2><p>Use Report on any content that breaks these rules, and Block to stop seeing someone. Moderators may remove content and restrict accounts.</p>
    </Prose>
  </>
);
export const NotFound = () => (
  <div className="container-x py-20 max-w-2xl" data-testid="not-found">
    <h1 className="text-4xl font-extrabold">Page not found</h1>
    <p className="mt-3 text-sub text-lg">The page you're looking for doesn't exist or has moved.</p>
    <div className="mt-6 flex gap-3"><Link to="/" className={btn()}>Go to home</Link><Link to="/help" className={btn("secondary")}>Help Centre</Link></div>
  </div>
);
